import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const UserContext = createContext();

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    // Initialize from local storage first for immediate UI render
    const localCart = JSON.parse(localStorage.getItem('cart') || '[]');
    const localWishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
    setCartItems(localCart);
    setWishlistItems(localWishlist);

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      fetchUserProfile(token, localCart);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserProfile = async (token, currentLocalCart = []) => {
    try {
      const response = await axios.get('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const fetchedUser = response.data.user;
        setUser(fetchedUser);
        setAddresses(fetchedUser.addresses || []);
        setIsAuthenticated(true);
        
        // Merge Wishlist
        if (fetchedUser.wishlist) {
          const dbWishlistIds = fetchedUser.wishlist.map(p => typeof p === 'object' ? p._id : p);
          setWishlistItems(dbWishlistIds);
          localStorage.setItem('wishlist', JSON.stringify(dbWishlistIds));
        }
        
        // Merge Cart - DB takes precedence unless we do complex sync
        if (fetchedUser.cart) {
          const dbCart = fetchedUser.cart.map(c => ({
            id: c.product._id || c.product,
            quantity: c.quantity,
            ...c.product // In case product is populated
          }));
          setCartItems(dbCart);
          localStorage.setItem('cart', JSON.stringify(dbCart));
          
          // Optionally POST back to API to sync any local-only items... but let's keep it simple.
        }
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      // Wait, we shouldn't logout automatically if API fails due to network. Only if 401.
      if (error.response && error.response.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = (userData, token, rememberMe) => {
    setUser(userData);
    setIsAuthenticated(true);
    if (rememberMe) {
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
    } else {
      sessionStorage.setItem('token', token);
      sessionStorage.setItem('user', JSON.stringify(userData));
    }
    // Sync upon login
    fetchUserProfile(token);
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    
    // Clear dynamic session data
    setCartItems([]);
    setWishlistItems([]);
    setAddresses([]);
    localStorage.removeItem('cart');
    localStorage.removeItem('wishlist');
  };

  // Profile Manipulators
  const updateProfile = async (profileData) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const res = await axios.put('/api/auth/profile', profileData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setUser(res.data.user);
        toast.success("Profile updated successfully!");
        return true;
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update profile");
      return false;
    }
  };

  // Address Manipulators
  const addAddress = async (addressData) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const res = await axios.post('/api/auth/addresses', addressData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setAddresses(res.data.addresses);
        setUser(prev => ({ ...prev, addresses: res.data.addresses }));
        toast.success("Address added successfully!");
        return true;
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add address");
      return false;
    }
  };

  const updateAddress = async (id, addressData) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const res = await axios.put(`/api/auth/addresses/${id}`, addressData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setAddresses(res.data.addresses);
        setUser(prev => ({ ...prev, addresses: res.data.addresses }));
        toast.success("Address updated successfully!");
        return true;
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update address");
      return false;
    }
  };

  const removeAddress = async (id) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const res = await axios.delete(`/api/auth/addresses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setAddresses(res.data.addresses);
        setUser(prev => ({ ...prev, addresses: res.data.addresses }));
        toast.success("Address removed successfully!");
        return true;
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to remove address");
      return false;
    }
  };

  // Cart Manipulators
  const addToCart = async (product, quantity = 1) => {
    if (!isAuthenticated) {
      navigate('/account');
      return;
    }
    const existingIndex = cartItems.findIndex(item => item.id === product._id || item._id === product._id);
    let newCart = [...cartItems];
    
    if (existingIndex > -1) {
      newCart[existingIndex] = {
        ...newCart[existingIndex],
        quantity: newCart[existingIndex].quantity + quantity
      };
    } else {
      newCart.push({
        id: product._id,
        _id: product._id,
        name: product.name,
        price: product.price,
        image: product.image,
        quantity: quantity,
        stock: product.stock
      });
    }
    
    setCartItems(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
    window.dispatchEvent(new Event('cartUpdated'));
    
    // Show success toast
    toast.success("Product added to cart!");

    if (isAuthenticated) {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      try {
        await axios.post('/api/auth/cart', {
            productId: product._id,
            quantity: quantity,
            action: 'add'
        }, { headers: { Authorization: `Bearer ${token}` }});
      } catch (e) {
        console.error("Failed to sync cart", e);
      }
    }
  };

  const removeFromCart = async (productId) => {
    const newCart = cartItems.filter(item => item.id !== productId && item._id !== productId);
    setCartItems(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
    window.dispatchEvent(new Event('cartUpdated'));

    if (isAuthenticated) {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      try {
        await axios.delete(`/api/auth/cart/${productId}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {
        console.error("Failed to sync cart", e);
      }
    }
  };

  // Wishlist Manipulators
  const addToWishlist = async (product) => {
    if (!isAuthenticated) {
      navigate('/account');
      return;
    }
    if (!wishlistItems.includes(product._id)) {
      const newWishlist = [...wishlistItems, product._id];
      setWishlistItems(newWishlist);
      localStorage.setItem('wishlist', JSON.stringify(newWishlist));
      window.dispatchEvent(new Event('wishlistUpdated'));
      
      // Show success toast
      toast.success("Product added to wishlist!");

      if (isAuthenticated) {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        try {
          await axios.post('/api/auth/wishlist', {
              productId: product._id
          }, { headers: { Authorization: `Bearer ${token}` }});
        } catch (e) {
            console.error("Failed to sync wishlist", e);
        }
      }
    } else {
      toast.info("Product is already in your wishlist");
    }
  };

  const removeFromWishlist = async (productId) => {
    const newWishlist = wishlistItems.filter(id => id !== productId);
    setWishlistItems(newWishlist);
    localStorage.setItem('wishlist', JSON.stringify(newWishlist));
    window.dispatchEvent(new Event('wishlistUpdated'));

    if (isAuthenticated) {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      try {
        await axios.delete(`/api/auth/wishlist/${productId}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {
          console.error("Failed to sync wishlist", e);
      }
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    fetchUserProfile,
    cartItems,
    setCartItems,
    wishlistItems,
    addToCart,
    removeFromCart,
    addToWishlist,
    removeFromWishlist,
    addresses,
    addAddress,
    updateAddress,
    removeAddress,
    updateProfile
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};